import { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import { Button, DenkerLogo } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { useProjectStore } from "@/stores/project-store";
import { useFrameField } from "@/stores/frames-selectors";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import {
  MENU_Z_INDEX_BASE,
  useInteractiveLayer,
} from "@/components/layout/interactive-layer";
import {
  addSourceHighlightText,
  appendHighlight,
  findHighlightsFrame,
  flashHighlightOverlay,
  removeHighlight,
} from "./selection-toolbar-helpers";
import { SelectionAskForm } from "@/components/selection/selection-ask-form";
import { SELECTION_COMPOSER_WIDTH } from "@/components/selection/selection-composer";
import {
  PillDivider,
  SELECTION_PILL_RADIUS,
} from "@/components/selection/selection-pill";

const NO_HIGHLIGHTS: string[] = [];

interface SelectionToolbarProps {
  selectedText: string;
  selectionRect: DOMRect;
  frameId: string;
  frameTitle: string;
  frameType: string;
  onClose: () => void;
}

/**
 * The Highlight / Ask Denker toolbar over a text selection, drawn on the document
 * body like the other menus: the desktop's floating frame windows sit on the
 * body above the workspace layout, so a toolbar left inside that layout would
 * draw under the very window whose text is selected.
 */
export function SelectionToolbar({
  selectedText,
  selectionRect,
  frameId,
  frameTitle,
  frameType,
  onClose,
}: SelectionToolbarProps) {
  const t = useT(canvasMessages);
  const [mode, setMode] = useState<"buttons" | "comment">("buttons");
  const [highlightSaving, setHighlightSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const toolbarLayer = useInteractiveLayer("selection-toolbar", {
    baseZIndex: MENU_Z_INDEX_BASE,
    tier: "menu",
  });

  const existingHighlights = useFrameField(
    frameId,
    (frame) =>
      (frame?.metadata?.highlightedTexts as string[] | undefined) ??
      NO_HIGHLIGHTS,
  );
  const isAlreadyHighlighted = existingHighlights.includes(selectedText);
  const highlightLabel = isAlreadyHighlighted
    ? t("unhighlight")
    : t("highlight");

  const buttonsTop = selectionRect.top - 48;
  const buttonsLeft = selectionRect.left + selectionRect.width / 2;
  const commentTop = Math.max(8, selectionRect.top - 10);
  const commentLeft = Math.max(
    8,
    Math.min(
      selectionRect.right + 12,
      window.innerWidth - SELECTION_COMPOSER_WIDTH - 18,
    ),
  );
  const toolbarTop = mode === "buttons" ? buttonsTop : commentTop;
  const toolbarLeft = mode === "buttons" ? buttonsLeft : commentLeft;

  const handleHighlight = useCallback(async () => {
    const { currentProject } = useProjectStore.getState();
    if (!currentProject) return;
    setHighlightSaving(true);
    try {
      flashHighlightOverlay(selectionRect);

      const existing = findHighlightsFrame();

      await appendHighlight(
        currentProject.id,
        existing?.id ?? null,
        selectedText,
        frameId,
        frameTitle,
        frameType,
      );

      await addSourceHighlightText(currentProject.id, frameId, selectedText);

      setToastMessage("savedToHighlights");
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        onClose();
      }, 1200);
    } finally {
      setHighlightSaving(false);
    }
  }, [selectedText, selectionRect, frameTitle, frameId, frameType, onClose]);

  const handleRemoveHighlight = useCallback(async () => {
    const { currentProject } = useProjectStore.getState();
    if (!currentProject) return;
    setHighlightSaving(true);
    try {
      await removeHighlight(currentProject.id, frameId, selectedText);

      setToastMessage("highlightRemoved");
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        onClose();
      }, 1200);
    } finally {
      setHighlightSaving(false);
    }
  }, [selectedText, frameId, onClose]);

  const [toastMessage, setToastMessage] = useState<
    "savedToHighlights" | "highlightRemoved"
  >("savedToHighlights");

  if (showToast) {
    return createPortal(
      <div
        data-selection-toolbar
        className="fixed -translate-x-1/2 rounded-menu border px-3 py-1.5 text-xs text-accent backdrop-blur-glass-sm"
        style={{ top: buttonsTop, left: buttonsLeft, ...toolbarLayer.style }}
        {...toolbarLayer.interactionProps}
        data-interactive="true"
        data-testid="selection-toolbar-toast"
      >
        {t(toastMessage)}
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      data-selection-toolbar
      className={cn(
        "pointer-events-auto fixed",
        mode === "buttons" && "-translate-x-1/2",
      )}
      style={{ top: toolbarTop, left: toolbarLeft, ...toolbarLayer.style }}
      {...toolbarLayer.interactionProps}
      data-interactive="true"
      data-testid="selection-toolbar"
    >
      {mode === "buttons" ? (
        <div
          className={cn(surfaceRoleClassName("menu"), SELECTION_PILL_RADIUS)}
          {...surfaceRoleAttributes("menu", {
            nativeLevel: "root",
            nativeGroup: "menus",
          })}
        >
          <div className="flex items-center gap-0.5 p-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="px-2.5"
              onClick={() => setMode("comment")}
              data-testid="selection-comment-btn"
            >
              <DenkerLogo variant="symbol" color="currentColor" height={13} />
              {t("selectionAskDenker")}
            </Button>
            <PillDivider />
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              aria-label={highlightLabel}
              title={highlightLabel}
              aria-pressed={isAlreadyHighlighted}
              disabled={highlightSaving}
              onClick={() =>
                void (isAlreadyHighlighted
                  ? handleRemoveHighlight()
                  : handleHighlight())
              }
              data-testid="selection-highlight-btn"
            >
              <Icons.Highlighter className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        </div>
      ) : (
        <SelectionAskForm
          frameId={frameId}
          frameTitle={frameTitle}
          frameType={frameType}
          selectedText={selectedText}
          onCancel={() => setMode("buttons")}
          onSent={onClose}
        />
      )}
    </div>,
    document.body,
  );
}
